import React from 'react';
import { ExternalLink, Linkedin, Globe } from 'lucide-react';

const AboutPage = () => {
  const founders = [
    {
      name: 'Sean Talluri',
      title: 'Founder & Minister',
      image: 'https://customer-assets.emergentagent.com/job_elbethel-worship/artifacts/u31qj9iw_Sean%20Profile%20pic.png',
      education: [
        'MBA in Information Systems - ICFAI Business School, Hyderabad, India',
        'MS in Technology Management - New Jersey Institute of Technology (NJIT)',
        'MA in Biblical Exposition - Liberty University',
        'Currently pursuing D.Min in Expository Preaching and Teaching - Liberty University'
      ],
      roles: [
        'Founder & Minister at El Bethel Ministries',
        'Founder of Christ Everywhere',
        'Founder of The Blanket Project',
        'Director of Product at Upbound Group',
        'Adjunct Professor of Product Management at Dallas Baptist University'
      ],
      bio: 'Sean humbly serves God\'s kingdom through various ministry and professional roles. Saved by grace at 16, his faith was deeply strengthened during a transformative season in Hyderabad, India in 2004. Like any child of God, Sean has struggled with his own shortcomings, but God\'s grace has sustained him through every season. The Lord blessed him with Rhoda, his faithful wife and ministry partner, whose love and support have been instrumental in his growth. In 2020, Sean experienced another significant period of transformation marked by repentance, reconciliation, and a renewed focus on his purpose and calling. This season of spiritual renewal opened doors for deeper service to the Lord and clarified his mission to serve God\'s people. Called to serve in both ministry and marketplace, Sean seeks to honor Christ through biblical teaching, mentoring future leaders, and developing platforms that serve believers. He considers it a privilege to serve alongside Rhoda in ministry, grateful for God\'s faithfulness in every season. His life verse from Philippians 4:13 reminds him that all strength comes from Christ.',
      links: [
        { type: 'website', url: 'https://seantalluri.com', label: 'Personal Website' },
        { type: 'linkedin', url: 'https://linkedin.com/in/seantalluri', label: 'LinkedIn Profile' }
      ]
    },
    {
      name: 'Rhoda Talluri',
      title: 'Co-Founder & Ministry Director',
      image: 'https://customer-assets.emergentagent.com/job_elbethel-worship/artifacts/o620sqlb_20250922_184400_0000.png',
      education: [
        'DScPT (Doctor of Science in Physical Therapy) - Andrews University'
      ],
      roles: [
        'Co-Founder at El Bethel Ministries',
        'Co-Founder at Christ Everywhere',
        'Co-Founder at The Blanket Project',
        'Physical Therapist at UT Southwestern Medical Center'
      ],
      bio: 'Rhoda humbly serves as a co-founder alongside Sean in multiple ministry initiatives, including El Bethel Ministries, Christ Everywhere, and The Blanket Project. Her heart for serving others is evident both in her professional calling as a Doctor of Physical Therapy, where she brings healing and hope to patients, and in her ministry work nurturing spiritual growth in the community. Rhoda believes that every act of service, whether in healthcare or ministry, is an opportunity to reflect Christ\'s love and compassion to those around her.',
      links: [
        { type: 'ministry', url: 'https://christeverywhere.org', label: 'Christ Everywhere' },
        { type: 'ministry', url: 'https://theblanket.us', label: 'The Blanket Project' }
      ]
    }
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              About El Bethel Ministries
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-sans">
              By God's grace, we have been called to serve His kingdom through biblical teaching, prayer support, 
              and compassionate community outreach. We are grateful stewards of the Gospel, committed to walking 
              humbly alongside believers in their spiritual journey while extending Christ's love to those in need.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 font-display">
              Our Mission
            </h2>
            <div className="bg-gray-50 rounded-2xl p-8 lg:p-12">
              <p className="text-xl lg:text-2xl text-gray-700 font-light leading-relaxed font-sans mb-6">
                "By God's grace, we humbly seek to serve His kingdom through biblical teaching, prayer support, 
                and demonstrating Christ's love to our community and those in need. We are called to be faithful 
                stewards of the Gospel, walking alongside believers in their spiritual journey while extending 
                compassionate care to the underserved."
              </p>
              <div className="text-[#f17b21] font-semibold font-sans">
                — El Bethel Ministries Mission Statement
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 font-display">
              Our Founders
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              Meet the dedicated leaders behind El Bethel Ministries, committed to serving God's kingdom 
              through biblical teaching and community outreach.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {founders.map((founder, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 hover:shadow-2xl transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 lg:w-32 lg:h-32 mx-auto mb-6 rounded-full overflow-hidden shadow-lg">
                    <img 
                      src={founder.image}
                      alt={founder.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2 font-display">{founder.name}</h3>
                  <p className="text-base lg:text-lg text-[#f17b21] font-semibold mb-4 font-sans">{founder.title}</p>
                </div>

                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed font-sans text-sm lg:text-base">{founder.bio}</p>

                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 font-sans">Current Roles:</h4>
                    <ul className="space-y-2">
                      {founder.roles.map((role, roleIndex) => (
                        <li key={roleIndex} className="text-gray-600 flex items-start text-sm lg:text-base">
                          <span className="w-2 h-2 bg-[#f17b21] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span className="font-sans">{role}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 font-sans">Education & Qualifications:</h4>
                    <ul className="space-y-2">
                      {founder.education.map((edu, eduIndex) => (
                        <li key={eduIndex} className="text-gray-600 flex items-start text-sm lg:text-base">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span className="font-sans">{edu}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {founder.links.length > 0 && (
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-3 font-sans">Connect:</h4>
                      <div className="flex flex-wrap gap-3">
                        {founder.links.map((link, linkIndex) => (
                          <a
                            key={linkIndex}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-300 font-sans"
                          >
                            {link.type === 'website' && <Globe className="w-4 h-4" />}
                            {link.type === 'linkedin' && <Linkedin className="w-4 h-4" />}
                            {link.type === 'ministry' && <ExternalLink className="w-4 h-4" />}
                            <span>{link.label}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 lg:mt-16">
            <div className="bg-gradient-to-r from-[#f17b21] to-[#ff9649] rounded-xl p-6 lg:p-8 text-white max-w-4xl mx-auto">
              <h3 className="text-xl lg:text-2xl font-bold mb-4 font-display">Heart for Ministry</h3>
              <blockquote className="text-base lg:text-lg italic mb-4 font-sans">
                "We are humbled by God's call to serve His people through ministry. Every aspect of our work - 
                from biblical teaching to community outreach - flows from a deep gratitude for God's grace 
                and a desire to faithfully steward the opportunities He has given us to serve others."
              </blockquote>
              <p className="font-semibold font-sans">- Sean & Rhoda Talluri</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;